import React, { useMemo } from 'react';
import moment from 'moment';
import useToggle from 'hooks/useToggle';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { numberToCurrency } from 'utils/misc';
import { roundToTwoDecimals } from 'utils/numbers';
import cancellationTypes from 'constants/cancellationTypes';

import * as Styled from './CancellationInfo.styled';

const CancellationInfo = (props) => {
  const {
    account,
    adfitNo,
    cancellationOption,
    cancellationFee,
    localisation,
    noticeStartDate,
  } = props;

  const { cancelReason, customCancelReason } = account;

  const { dateFormat } = localisation.locales;

  const [isSuccessAlertOpen, toggleSuccessAlert] = useToggle(true);

  const formattedNoticeStartDate = useMemo(() => {
    if (!noticeStartDate) return '-';
    return moment(noticeStartDate).format(dateFormat);
  }, [dateFormat, noticeStartDate]);

  const alertMessages = useMemo(() => {
    if (!cancellationOption) return [];

    const { value } = cancellationOption;

    switch (value) {
      case cancellationTypes.COLLECT_DS_FEE:
        return [
          'Cancellation set up complete!',
          'Account will be cancelled after the Debitsuccess fee is collected.',
        ];
      case cancellationTypes.COLLECT_OVERDUE:
        return [
          'Cancellation was set up successfully.',
          `Account will be cancelled after the overdue amount ${
            cancellationFee ? 'and the cancellation fee' : ''
          } is collected.`,
        ];
      case cancellationTypes.COLLECT_CANCELLATION_FEE:
        return [
          'Cancellation set up complete!',
          'Account will be cancelled after the cancellation fee is collected.',
        ];
      case cancellationTypes.WITH_NOTICE_PERIOD ||
        cancellationTypes.SET_TO_TERM:
        return [
          'Cancellation set up complete!',
          'Reversals, overdues, suspensions or catch ups may delay the final payment date.',
        ];
      default:
        return ['Account was cancelled successfully.'];
    }
  }, [cancellationOption, cancellationFee]);

  return (
    <div data-testid={'cancellation-info'}>
      {isSuccessAlertOpen && (
        <Styled.InfoAlert
          variant={'success'}
          dismissable={true}
          onClose={toggleSuccessAlert}
        >
          {alertMessages.map((val, index) => (
            <Styled.InfoAlertMessage key={index}>{val}</Styled.InfoAlertMessage>
          ))}
        </Styled.InfoAlert>
      )}
      <Styled.Paper>
        <Styled.PaperHeader>
          <Styled.PaperHeaderText>{'Cancel Account'}</Styled.PaperHeaderText>
        </Styled.PaperHeader>
        <Styled.PaperBodyOverload>
          {cancellationOption && (
            <Styled.BodySectionWrapper>
              <Styled.InfoLabel>{'Cancellation type'}</Styled.InfoLabel>
              <Styled.InfoText>{cancellationOption.label}</Styled.InfoText>
              <Styled.InfoSubTextContainer>
                {cancellationOption.subLabels &&
                  cancellationOption.subLabels.map((val, index) => (
                    <Styled.InfoSubText key={index}>{val}</Styled.InfoSubText>
                  ))}
              </Styled.InfoSubTextContainer>
            </Styled.BodySectionWrapper>
          )}
          {cancellationOption &&
            (cancellationOption.value === cancellationTypes.COLLECT_OVERDUE ||
              cancellationOption.value ===
                cancellationTypes.COLLECT_CANCELLATION_FEE) && (
              <Styled.BodySectionWrapper>
                <Styled.InfoLabel>{'Cancellation fee'}</Styled.InfoLabel>
                <Styled.InfoText>
                  {!cancellationFee
                    ? '-'
                    : `${numberToCurrency(
                        roundToTwoDecimals(cancellationFee),
                        localisation
                      )}`}
                </Styled.InfoText>
              </Styled.BodySectionWrapper>
            )}
          {cancellationOption &&
            cancellationOption.value ===
              cancellationTypes.WITH_NOTICE_PERIOD && (
              <Styled.BodySectionWrapper>
                <Styled.InfoLabel>{'Start notice period on'}</Styled.InfoLabel>
                <Styled.InfoText>{formattedNoticeStartDate}</Styled.InfoText>
              </Styled.BodySectionWrapper>
            )}

          <Styled.BodySectionWrapper>
            <Styled.InfoLabel>{'Cancellation reason'}</Styled.InfoLabel>
            <Styled.InfoText>
              {customCancelReason
                ? customCancelReason
                : !cancelReason || cancelReason === 'Facility Request'
                ? 'Business request'
                : cancelReason}
            </Styled.InfoText>
          </Styled.BodySectionWrapper>
        </Styled.PaperBodyOverload>
      </Styled.Paper>
      <Link to={`/Customers/${adfitNo}/Overview`}>
        <Styled.BackButton variant={'primary'}>{'Back'}</Styled.BackButton>
      </Link>
    </div>
  );
};

CancellationInfo.propTypes = {
  account: PropTypes.object.isRequired,
  adfitNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cancellationOption: PropTypes.shape({
    label: PropTypes.string.isRequired,
    subLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
    value: PropTypes.number.isRequired,
  }),
  cancellationFee: PropTypes.number,
  localisation: PropTypes.object,
  noticeStartDate: PropTypes.string,
  cancelDaysNotice: PropTypes.number,
};

CancellationInfo.defaultProps = {
  cancellationFee: null,
  noticeStartDate: null,
  cancelDaysNotice: null,
};

export default CancellationInfo;
